import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { apiJson } from './apiClient';

const OtherServiceSalesContext = createContext(null);

export function useOtherServiceSales() {
  const ctx = useContext(OtherServiceSalesContext);
  if (!ctx) {
    throw new Error('useOtherServiceSales must be used within OtherServiceSalesProvider');
  }
  return ctx;
}

export function OtherServiceSalesProvider({ children }) {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setSales([]);
      return;
    }
    setLoading(true);
    try {
      const data = await apiJson('/other-service-sales');
      setSales(Array.isArray(data.sales) ? data.sales : []);
    } catch {
      setSales([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const recordSale = useCallback(
    async ({ serviceCode, description, amountTzs }) => {
      const body = {
        serviceCode: String(serviceCode || '').trim(),
        description: description != null ? String(description).trim() : '',
        amountTzs: Number(amountTzs),
      };
      await apiJson('/other-service-sales', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      await refresh();
    },
    [refresh]
  );

  const value = useMemo(
    () => ({
      sales,
      otherServiceSalesLoading: loading,
      refreshOtherServiceSales: refresh,
      recordSale,
    }),
    [sales, loading, refresh, recordSale]
  );

  return <OtherServiceSalesContext.Provider value={value}>{children}</OtherServiceSalesContext.Provider>;
}
